import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { credentials } from '@cwos/api-client';
import {
  ErrorPanel,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  EMPTY_FILE_DRAFT,
  expiryLabel,
  filePayload,
  formatCalendarDate,
  messageOf,
  renewPayload,
  statusPill,
  validateFileDraft,
  type FileDraft,
} from './credentialsLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * One worker's credentials: what they hold and are required to hold, together
 * (a type never appears twice, whether held, required, or both — the server's
 * own rule, see `workerCredentialLines`), plus the filing/renewal/verification
 * history and the actions this caller is allowed to attempt.
 *
 * SEPARATION OF DUTIES IS SERVER-ENFORCED. Nobody verifies or rejects their own
 * credential, regardless of role — the server refuses it outright, and that
 * refusal is shown verbatim rather than guessed at here.
 */
export function CredentialsWorkerDetail() {
  const params = useParams({ strict: false }) as { workerId?: string };
  const workerId = params.workerId ?? '';
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('credential:manage');
  const canVerify = has('credential:verify');

  const [filing, setFiling] = useState(false);
  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FileDraft>(EMPTY_FILE_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['credentials', 'worker', workerId],
    queryFn: () => credentials.workerDetail(workerId),
    enabled: workerId !== '',
  });

  const types = useQuery({
    queryKey: ['credentials', 'types', 'all'],
    queryFn: () => credentials.types(),
    enabled: canManage && (filing || renewingId !== null),
  });

  function resetForm() {
    setFiling(false);
    setRenewingId(null);
    setDraft(EMPTY_FILE_DRAFT);
    setFormError(null);
  }

  const file = useMutation({
    mutationFn: () => credentials.fileCredential(filePayload(workerId, draft)),
    onSuccess: () => {
      resetForm();
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const renew = useMutation({
    mutationFn: (id: string) => credentials.renewCredential(id, renewPayload(draft)),
    onSuccess: () => {
      resetForm();
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const verify = useMutation({
    mutationFn: (id: string) => credentials.verifyCredential(id),
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setActionError(messageOf(err)),
  });

  const reject = useMutation({
    mutationFn: (id: string) => credentials.rejectCredential(id, rejectReason.trim()),
    onSuccess: () => {
      setRejectingId(null);
      setRejectReason('');
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['credentials'] });
    },
    onError: (err: unknown) => setActionError(messageOf(err)),
  });

  const selectedType = types.data?.find((t) => t.id === draft.credentialTypeId);
  const errors = validateFileDraft(draft, selectedType);
  const canSubmit = Object.keys(errors).length === 0;

  const lineColumns: Column<credentials.WorkerCredentialLine>[] = [
    {
      key: 'type',
      header: 'Credential',
      render: (l) => (
        <span>
          {l.credentialTypeName}
          {l.required ? null : <span className="rn-muted"> · not required</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => {
        const p = statusPill(l);
        return (
          <Pill variant={p.variant} dot>
            {p.label}
          </Pill>
        );
      },
    },
    { key: 'expiry', header: 'Expiry', render: (l) => expiryLabel(l.expiresOn, l.daysUntilExpiry) },
  ];

  const credColumns: Column<credentials.CredentialView>[] = [
    { key: 'type', header: 'Credential', render: (c) => c.credentialTypeName ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (c) => {
        const p = statusPill(c);
        return (
          <Pill variant={p.variant} dot>
            {p.label}
          </Pill>
        );
      },
    },
    { key: 'number', header: 'Number', render: (c) => c.credentialNumber ?? '—' },
    { key: 'issuer', header: 'Issuing authority', render: (c) => c.issuingAuthority ?? '—' },
    { key: 'issued', header: 'Issued', render: (c) => formatCalendarDate(c.issuedOn) },
    { key: 'expires', header: 'Expires', render: (c) => formatCalendarDate(c.expiresOn) },
    {
      key: 'evidence',
      header: 'Evidence',
      render: (c) => (c.hasEvidence ? 'On file' : 'None on file'),
    },
    {
      key: 'actions',
      header: '',
      render: (c) => (
        <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
          {canManage && c.status !== 'superseded' ? (
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setRenewingId(c.id);
                setFiling(false);
                setDraft({
                  credentialTypeId: c.credentialTypeId,
                  credentialNumber: c.credentialNumber ?? '',
                  issuingAuthority: c.issuingAuthority ?? '',
                  issuedOn: '',
                  expiresOn: '',
                });
                setFormError(null);
              }}
            >
              Renew
            </button>
          ) : null}
          {canVerify && c.status === 'pending_verification' ? (
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={verify.isPending}
              onClick={() => verify.mutate(c.id)}
            >
              {verify.isPending ? 'Verifying…' : 'Verify'}
            </button>
          ) : null}
          {canVerify && c.status === 'pending_verification' ? (
            rejectingId === c.id ? null : (
              <button type="button" className="rn-btn-quiet" onClick={() => setRejectingId(c.id)}>
                Reject
              </button>
            )
          ) : null}
        </div>
      ),
    },
  ];

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 1080 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <LoadFailure
          error={detail.error}
          message="We couldn’t load this worker’s credentials just now."
          onRetry={() => void detail.refetch()}
          mainRead
        />
      </div>
    );
  }

  const data = detail.data;

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader title={data.displayName} blurb={data.verificationMeaning} />

      <SettingsCard>
        <div className="cmp-stats">
          <Stat label="Active" value={data.summary.active} />
          <Stat label="Expiring soon" value={data.summary.expiringSoon} />
          <Stat label="Expired" value={data.summary.expired} />
          <Stat label="Pending verification" value={data.summary.pendingVerification} />
          <Stat label="Missing" value={data.summary.missing} />
        </div>
        <div style={{ marginTop: 'var(--space-8)' }}>
          <Pill variant={data.summary.fullyCredentialed ? 'good' : 'urgent'} dot>
            {data.summary.fullyCredentialed ? 'Fully credentialed' : 'Not fully credentialed'}
          </Pill>
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Required and held</h2>
        {data.lines.length === 0 ? (
          <SuccessEmpty
            heading="Nothing tracked yet"
            message="No credential requirement applies to this worker, and nothing has been filed."
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)' }}>
            <TableCard columns={lineColumns} rows={data.lines} rowKey={(l) => l.credentialTypeId} />
          </div>
        )}
      </SettingsCard>

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Filing history</h2>
          {canManage && !filing && !renewingId ? (
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setFiling(true);
                setRenewingId(null);
                setDraft(EMPTY_FILE_DRAFT);
                setFormError(null);
              }}
            >
              File a credential
            </button>
          ) : null}
        </div>

        {actionError ? <ErrorPanel message={actionError} /> : null}

        {filing || renewingId ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canSubmit) return;
              if (renewingId) renew.mutate(renewingId);
              else file.mutate();
            }}
          >
            {renewingId ? (
              <p className="rn-muted" style={{ fontSize: 13 }}>
                Renewing files a replacement and supersedes the one it replaces — the old record
                keeps its own dates and verification as history.
              </p>
            ) : (
              <SettingsField label="Credential type" error={errors['credentialTypeId']}>
                <select
                  style={{ marginBottom: 0 }}
                  value={draft.credentialTypeId}
                  onChange={(e) => setDraft({ ...draft, credentialTypeId: e.target.value })}
                >
                  <option value="">Choose a type</option>
                  {(types.data ?? [])
                    .filter((t) => t.status === 'active')
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </SettingsField>
            )}
            <div className="org-create-grid">
              <SettingsField
                label={`Credential number${selectedType?.requiresCredentialNumber ? '' : ' (optional)'}`}
                error={errors['credentialNumber']}
              >
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.credentialNumber}
                  onChange={(e) => setDraft({ ...draft, credentialNumber: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Issuing authority (optional)">
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.issuingAuthority}
                  onChange={(e) => setDraft({ ...draft, issuingAuthority: e.target.value })}
                />
              </SettingsField>
            </div>
            <div className="org-create-grid">
              <SettingsField label="Issued on (optional)">
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  value={draft.issuedOn}
                  onChange={(e) => setDraft({ ...draft, issuedOn: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label={`Expires on${selectedType?.requiresExpiration ? '' : ' (optional)'}`}
                error={errors['expiresOn']}
              >
                <input
                  style={{ marginBottom: 0 }}
                  type="date"
                  value={draft.expiresOn}
                  onChange={(e) => setDraft({ ...draft, expiresOn: e.target.value })}
                />
              </SettingsField>
            </div>
            <p className="rn-muted" style={{ fontSize: 13 }}>
              This lands as pending verification — nothing self-verifies, including a credential an
              administrator files on somebody's behalf.
            </p>
            {formError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {formError}
              </p>
            ) : null}
            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!canSubmit || file.isPending || renew.isPending}
              >
                {file.isPending || renew.isPending
                  ? 'Saving…'
                  : renewingId
                    ? 'Save renewal'
                    : 'File credential'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {rejectingId ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (rejectReason.trim()) reject.mutate(rejectingId);
            }}
          >
            <SettingsField label="Reason — the worker will see this">
              <textarea
                style={{ marginBottom: 0 }}
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                autoFocus
              />
            </SettingsField>
            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={rejectReason.trim() === '' || reject.isPending}
              >
                {reject.isPending ? 'Sending…' : 'Send back'}
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setRejectingId(null);
                  setRejectReason('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {data.credentials.length === 0 ? (
          <SuccessEmpty
            heading="Nothing filed yet"
            message="No credential has been filed for this worker."
          />
        ) : (
          <div style={{ marginTop: 'var(--space-12)' }}>
            <TableCard
              columns={credColumns}
              rows={data.credentials}
              rowKey={(c) => c.id}
              caption="Every credential filed for this worker, most recent first"
            />
            {data.credentials.some((c) => c.status === 'rejected' && c.rejectionReason) ? (
              <ul className="rn-muted" style={{ marginTop: 'var(--space-10)', fontSize: 13 }}>
                {data.credentials
                  .filter((c) => c.status === 'rejected' && c.rejectionReason)
                  .map((c) => (
                    <li key={c.id}>
                      {c.credentialTypeName}: {c.rejectionReason}
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
        )}
      </SettingsCard>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="cmp-stat">
      <span className="cmp-stat-value">{value}</span>
      <span className="cmp-stat-label">{label}</span>
    </div>
  );
}
